import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  PushPin as PinIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useClubs } from '../context/AppContext';
import DiscussionService, { Discussion } from '../services/discussionService';

interface CategoryFilter {
  key: string | undefined;
  label: string;
  icon: string;
}

const categories: CategoryFilter[] = [
  { key: undefined, label: 'All', icon: '📋' },
  { key: 'general', label: 'General', icon: '💬' },
  { key: 'technical', label: 'Technical', icon: '🔧' },
  { key: 'events', label: 'Events', icon: '📅' },
  { key: 'support', label: 'Support', icon: '🆘' },
];


interface DiscussionCardProps {
  discussion: Discussion;
  onPress: (discussionId: string) => void;
  onAuthorPress: (authorId: string) => void;
}

const DiscussionCard: React.FC<DiscussionCardProps> = ({
  discussion,
  onPress,
  onAuthorPress,
}) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'general': return '#007AFF';
      case 'technical': return '#5856D6';
      case 'events': return '#FF9500';
      case 'support': return '#34C759';
      default: return '#8E8E93';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getLastActivityTime = () => {
    if (discussion.lastReply) {
      return formatTime(discussion.lastReply);
    }
    return formatTime(discussion.timestamp);
  };

  return (
    <Card
      sx={{
        mb: { xs: 1, sm: 2 },
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 3 },
        cursor: 'pointer',
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: { xs: 'none', sm: '0 8px 24px rgba(0,0,0,0.12)' },
          transform: { xs: 'none', sm: 'translateY(-2px)' },
          '& .discussion-title': {
            color: { xs: '#1877f2', sm: '#166fe5' }
          }
        },
        '&:active': {
          transform: { xs: 'scale(0.98)', sm: 'translateY(-2px) scale(0.98)' }
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: discussion.isPinned ? '2px solid #FFD60A' : { xs: 'none', sm: '1px solid #e4e6ea' },
        borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
        boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.08)' },
        backgroundColor: { xs: 'white', sm: 'white' }
      }}
      onClick={() => onPress(discussion.id)}
    >
      <CardContent sx={{
        p: { xs: 2, sm: 3 },
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {discussion.isPinned && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 1.5, sm: 2 },
            p: { xs: 0.75, sm: 1 },
            backgroundColor: '#FFF3CD',
            borderRadius: { xs: 0.5, sm: 1 },
            border: '1px solid #FFECB5'
          }}>
            <PinIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: '#856404' }} />
            <Typography variant="caption" sx={{
              fontWeight: 600,
              color: '#856404',
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}>
              Pinned Discussion
            </Typography>
          </Box>
        )}

        {/* Category Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
          <Chip
            icon={
              <Box component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                {discussion.category === 'general' ? '💬' :
                 discussion.category === 'technical' ? '🔧' :
                 discussion.category === 'events' ? '📅' : '🆘'}
              </Box>
            }
            label={discussion.category.charAt(0).toUpperCase() + discussion.category.slice(1)}
            size="small"
            sx={{
              backgroundColor: getCategoryColor(discussion.category) + '20',
              color: getCategoryColor(discussion.category),
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 24, sm: 26 }
            }}
          />
        </Box>

        {/* Discussion Title */}
        <Typography
          className="discussion-title"
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.75, sm: 1 },
            fontSize: { xs: '1rem', sm: '1.1rem' },
            color: '#1877f2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
            transition: 'color 0.2s ease'
          }}
        >
          {discussion.title}
        </Typography>

        {/* Discussion Preview */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: { xs: 2, sm: 3 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: { xs: 2, sm: 3 },
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.5,
            fontSize: { xs: '0.85rem', sm: '0.875rem' }
          }}
        >
          {discussion.content}
        </Typography>

        {/* Footer */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: { xs: 1.5, sm: 2 },
          borderTop: '1px solid #f0f2f5'
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
              flex: 1,
              minWidth: 0
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAuthorPress(discussion.authorId);
            }}
          >
            <Avatar sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              bgcolor: '#1877f2',
              fontWeight: 600
            }}>
              {discussion.authorName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {discussion.authorName}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexShrink: 0
          }}>
            {discussion.replies > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>💬</Box>
                <Typography variant="caption" color="text.secondary" sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}>
                  {discussion.replies}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              whiteSpace: 'nowrap'
            }}>
              {getLastActivityTime()}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};


const ClubDiscussionsPage: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { clubs } = useClubs();

  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);

  const club = clubs.find(c => c.id === clubId);

  const loadDiscussions = useCallback(async () => {
    if (!clubId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await DiscussionService.getClubDiscussions(clubId, 1, 50);

      // Handle response format from backend
      if (response && response.discussions && Array.isArray(response.discussions)) {
        setDiscussions(response.discussions);
      } else {
        console.warn('Unexpected response format:', response);
        setDiscussions([]);
      }
    } catch (error: any) {
      console.error('Failed to load discussions:', error);
      setError('Failed to load discussions. Please try again.');
      setDiscussions([]);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadDiscussions();
  }, [loadDiscussions]);


  const handleDiscussionPress = (discussionId: string) => {
    navigate(`/clubs/${clubId}/discussions/${discussionId}`);
  };

  const handleAuthorPress = (authorId: string) => {
    // TODO: Navigate to author profile
    console.log('Navigate to author profile:', authorId);
  };

  const filteredDiscussions = discussions.filter(discussion => {
    if (!selectedCategory) return true;
    return discussion.category === selectedCategory;
  });

  // For debugging - can be removed in production
  // console.log('Discussions:', discussions.length, 'Filtered:', filteredDiscussions.length);

  const getCategoryCount = (categoryKey: string | undefined) => {
    if (!categoryKey) return discussions.length;
    return discussions.filter(d => d.category === categoryKey).length;
  };

  if (!club) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Club not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: { xs: 'white', sm: '#f0f2f5' },
      pb: { xs: 10, sm: 2 },
      overflow: 'hidden',
      maxWidth: '100vw',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <IconButton
            onClick={() => navigate(`/clubs/${clubId}`)}
            size="small"
            sx={{
              backgroundColor: '#f0f2f5',
              p: { xs: 0.5, sm: 1.2 },
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#e4e6ea'
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1877f2',
                fontSize: { xs: '0.95rem', sm: '1.4rem' },
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {club.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.85rem' },
                fontWeight: 500
              }}
            >
              Discussions ({discussions.length})
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: { xs: '0.85rem', sm: '1.2rem' } }} />}
            onClick={() => navigate(`/clubs/${clubId}/create-discussion`)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#1877f2',
              borderRadius: 2,
              px: { xs: 1, sm: 3 },
              py: { xs: 0.5, sm: 1.2 },
              fontSize: { xs: '0.65rem', sm: '0.9rem' },
              fontWeight: 600,
              minWidth: { xs: '55px', sm: 'auto' },
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#166fe5'
              },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0.2, sm: 1 },
                marginLeft: 0
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              New Discussion
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              New
            </Box>
          </Button>
        </Box>

        {/* Category Filter */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1 },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <FilterListIcon sx={{
            color: '#6b7280',
            fontSize: { xs: '1.1rem', sm: '1.4rem' },
            flexShrink: 0
          }} />
          <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
            <Select
              value={selectedCategory || 'all'}
              onChange={(e) => setSelectedCategory(e.target.value === 'all' ? undefined : e.target.value)}
              sx={{
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 600,
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e2e8f0'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1877f2'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1877f2'
                },
                '& .MuiSelect-select': {
                  py: { xs: 0.9, sm: 1.2 },
                  px: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1 }
                }
              }}
            >
              {categories.map((category) => (
                <MenuItem
                  key={category.key || 'all'}
                  value={category.key || 'all'}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    fontWeight: 600,
                    py: 1.5
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%'
                  }}>
                    <Box component="span" sx={{ fontSize: '1.1rem' }}>
                      {category.icon}
                    </Box>
                    <Typography sx={{
                      flex: 1,
                      fontWeight: 'inherit',
                      fontSize: 'inherit'
                    }}>
                      {category.label}
                    </Typography>
                    {getCategoryCount(category.key) > 0 && (
                      <Chip
                        label={getCategoryCount(category.key)}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 24,
                          backgroundColor: '#1877f2',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          '& .MuiChip-label': {
                            px: 0.75
                          }
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 800 },
        mx: 'auto',
        px: { xs: 0, sm: 3 },
        py: { xs: 1, sm: 3 },
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {error && (
          <Alert severity="error" sx={{
            mb: { xs: 2, sm: 3 },
            mx: { xs: 2, sm: 0 },
            borderRadius: { xs: 0, sm: 1 }
          }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, sm: 8 } }}>
            <CircularProgress />
          </Box>
        ) : filteredDiscussions.length > 0 ? (
          <Box sx={{ px: { xs: 0, sm: 0 } }}>
            {filteredDiscussions.map((discussion) => (
              <DiscussionCard
                key={discussion.id}
                discussion={discussion}
                onPress={handleDiscussionPress}
                onAuthorPress={handleAuthorPress}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{
            textAlign: 'center',
            py: { xs: 6, sm: 8 },
            px: { xs: 3, sm: 0 }
          }}>
            <Typography variant="h1" sx={{
              fontSize: { xs: '3rem', sm: '4rem' },
              mb: { xs: 1.5, sm: 2 }
            }}>
              💭
            </Typography>
            <Typography variant="h5" sx={{
              fontWeight: 600,
              mb: { xs: 0.75, sm: 1 },
              fontSize: { xs: '1.3rem', sm: '1.5rem' }
            }}>
              {selectedCategory ? `No ${selectedCategory} discussions yet` : 'No discussions yet'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{
              mb: { xs: 2.5, sm: 3 },
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.6
            }}>
              {selectedCategory
                ? `Start the first ${selectedCategory} discussion for this club!`
                : 'Start a conversation with your fellow club members!'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/clubs/${clubId}/create-discussion`)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#1877f2',
                borderRadius: { xs: 2, sm: 1 },
                px: { xs: 3, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#166fe5'
                }
              }}
            >
              Start First Discussion
            </Button>
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default ClubDiscussionsPage;